/**
 * Goal form milestones section — auto/manual milestone configuration.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { generateAutoMilestones } from '@turnifly/shared/utils'

const MONTH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const

interface MilestoneInput {
  month: number
  target: number
}

interface GoalFormMilestonesProps {
  autoMilestones: boolean
  weeklyMilestones: boolean
  manualTargets: MilestoneInput[]
  targetValue: number
  onAutoChange: (v: boolean) => void
  onWeeklyChange: (v: boolean) => void
  onManualTargetChange: (month: number, value: number) => void
}

export const GoalFormMilestones: React.FC<GoalFormMilestonesProps> = ({
  autoMilestones, weeklyMilestones, manualTargets, targetValue,
  onAutoChange, onWeeklyChange, onManualTargetChange,
}) => {
  const { t } = useTranslation()
  const currentMonth = new Date().getMonth() + 1
  const autoTargets = generateAutoMilestones(targetValue, currentMonth)

  return (
    <div className="space-y-4">
      {/* Auto milestones toggle */}
      <button
        onClick={() => onAutoChange(!autoMilestones)}
        className="w-full flex items-center justify-between py-2.5"
      >
        <span className="text-xs text-text">{t('goals.autoMilestones')}</span>
        <div className={`w-9 h-5 rounded-full transition-colors relative ${autoMilestones ? 'bg-primary' : 'bg-white/[0.12]'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoMilestones ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </div>
      </button>

      {/* Distribution preview or manual inputs */}
      {autoMilestones ? (
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3">
          <p className="text-[10px] text-text-hint mb-2">{t('goals.autoMilestones')}</p>
          <div className="flex items-end gap-1 h-12">
            {autoTargets.map((ms) => {
              const height = targetValue > 0
                ? Math.max(4, (ms.target / targetValue) * 100)
                : 4
              return (
                <div key={ms.month} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className="w-full rounded-sm bg-primary/30"
                    style={{ height: `${height}%` }}
                  />
                  <span className="text-[7px] text-text-hint tabular-nums">
                    {t(`goals.months.${ms.month}`)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div>
          <p className="text-[10px] text-text-hint mb-2">{t('goals.manualMilestones')}</p>
          <div className="grid grid-cols-4 gap-2">
            {MONTH_KEYS.map((month) => {
              const existing = manualTargets.find((m) => m.month === month)
              return (
                <div key={month} className="text-center">
                  <label className="text-[9px] text-text-hint block mb-1">
                    {t(`goals.months.${month}`)}
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={existing?.target ?? ''}
                    onChange={(e) => onManualTargetChange(month, Number(e.target.value) || 0)}
                    placeholder="0"
                    min={0}
                    className="w-full h-8 px-1.5 text-center rounded-lg bg-white/[0.06] border border-white/[0.08] text-xs text-text placeholder:text-text-hint/30 tabular-nums outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Weekly milestones toggle */}
      <button
        onClick={() => onWeeklyChange(!weeklyMilestones)}
        className="w-full flex items-center justify-between py-2.5"
      >
        <span className="text-xs text-text">{t('goals.weeklyMilestones')}</span>
        <div className={`w-9 h-5 rounded-full transition-colors relative ${weeklyMilestones ? 'bg-primary' : 'bg-white/[0.12]'}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${weeklyMilestones ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </div>
      </button>
    </div>
  )
}
